import type { Fund } from '@/domain/fundraising';
import type { FundFormValues, InvestmentFundRecord } from '@/types/fund';

/** 后端基金记录 → 领域 Fund（id 用后端 uuid；status 映射后端 phase，枚举值两端一致）。 */
export const toDomainFund = (record: InvestmentFundRecord): Fund => ({
  id: record.uuid,
  generalPartnerUuid: record.generalPartnerUuid ?? null,
  code: record.code,
  currency: record.currency,
  name: record.name,
  shortName: record.shortName ?? record.name,
  type: record.type ?? '—',
  registrationType: record.registrationType ?? undefined,
  organizationForm: record.organizationForm ?? undefined,
  manager: record.manager ?? '',
  owner: record.ownerName ?? '',
  target: Number(record.targetAmount ?? 0),
  status: record.phase,
  establishedAt: record.establishedAt ?? '',
  updatedAt: record.updatedAt ?? '',
  duration: record.durationYears ? `${record.durationYears}年` : '',
  region: record.region ?? '',
  strategy: record.strategy ?? '',
  industries: record.industries ?? [],
  minimum: Number(record.minimumAmount ?? 0),
  firstCloseTarget: Number(record.firstCloseTarget ?? 0),
  firstCloseAt: record.firstCloseAt ?? '',
  finalCloseAt: record.finalCloseAt ?? '',
  controlType: record.controlType ?? '自管',
  ourShareRatio:
    record.ourShareRatio == null || record.ourShareRatio === ''
      ? undefined
      : Number(record.ourShareRatio),
  isAbsoluteControl: record.isAbsoluteControl,
  controlRemark: record.controlRemark ?? undefined,
  fundKind: record.fundKind ?? undefined,
  custodianUuid: record.custodianUuid ?? null,
  fundManagerUuid: record.fundManagerUuid ?? null,
  managerUnifiedSocialCreditCode: record.managerUnifiedSocialCreditCode ?? null,
  managementEntity: record.managementEntity ?? null,
  partnershipEntity: record.partnershipEntity ?? null,
  partnershipOrganizationUuid: record.partnershipOrganizationUuid ?? null,
  exitPeriodEndAt: record.exitPeriodEndAt ?? null,
  strategyType: record.strategyType ?? null,
  registrationNumber: record.registrationNumber ?? null,
  registrationDate: record.registrationDate ?? null,
  investmentSubjectUuid: record.investmentSubjectUuid ?? null,
  managerPlatformUuid: record.managerPlatformUuid ?? null,
  fundStatus: record.status ?? '启用',
});

const parseDurationYears = (duration: string): number | undefined => {
  const match = duration.match(/\d+/);
  return match ? Number(match[0]) : undefined;
};

/** 领域 Fund → 编辑表单初始值。 */
export const fundToFormValues = (fund: Fund): FundFormValues => ({
  generalPartnerUuid: fund.generalPartnerUuid ?? null,
  code: fund.code ?? '',
  name: fund.name,
  shortName: fund.shortName === fund.name ? undefined : fund.shortName,
  type: fund.type === '—' ? undefined : fund.type,
  registrationType: fund.registrationType || undefined,
  organizationForm: fund.organizationForm || undefined,
  manager: fund.manager || undefined,
  ownerName: fund.owner || undefined,
  targetAmount: fund.target || undefined,
  minimumAmount: fund.minimum || undefined,
  firstCloseTarget: fund.firstCloseTarget || undefined,
  durationYears: parseDurationYears(fund.duration),
  region: fund.region || undefined,
  strategy: fund.strategy || undefined,
  industries: fund.industries,
  establishedAt: fund.establishedAt || undefined,
  firstCloseAt: fund.firstCloseAt || undefined,
  finalCloseAt: fund.finalCloseAt || undefined,
  controlType: fund.controlType ?? '自管',
  ourShareRatio: fund.ourShareRatio,
  controlRemark: fund.controlRemark,
  fundKind: fund.fundKind,
  custodianUuid: fund.custodianUuid ?? undefined,
  fundManagerUuid: fund.fundManagerUuid ?? undefined,
  managerUnifiedSocialCreditCode:
    fund.managerUnifiedSocialCreditCode ?? undefined,
  managementEntity: fund.managementEntity ?? undefined,
  partnershipEntity: fund.partnershipEntity ?? undefined,
  partnershipOrganizationUuid: fund.partnershipOrganizationUuid ?? undefined,
  exitPeriodEndAt: fund.exitPeriodEndAt ?? undefined,
  strategyType: fund.strategyType ?? undefined,
  registrationNumber: fund.registrationNumber ?? undefined,
  registrationDate: fund.registrationDate ?? undefined,
  investmentSubjectUuid: fund.investmentSubjectUuid ?? undefined,
  managerPlatformUuid: fund.managerPlatformUuid ?? undefined,
});

const nullableTextFields: Array<keyof FundFormValues> = [
  'shortName',
  'type',
  'registrationType',
  'organizationForm',
  'managementEntity',
  'partnershipEntity',
  'manager',
  'ownerName',
  'managerUnifiedSocialCreditCode',
  'region',
  'strategy',
  'strategyType',
  'controlRemark',
  'fundKind',
];

const nullableValueFields: Array<keyof FundFormValues> = [
  'generalPartnerUuid',
  'custodianUuid',
  'fundManagerUuid',
  'investmentSubjectUuid',
  'managerPlatformUuid',
  'establishedAt',
  'exitPeriodEndAt',
  'targetAmount',
  'firstCloseTarget',
  'ourShareRatio',
  'durationYears',
];

const hasOwn = (value: object, key: PropertyKey) => Object.hasOwn(value, key);

/** Keeps a form clear action as an explicit null instead of a rejected empty string. */
export const normalizeFundFormValues = <T extends Partial<FundFormValues>>(
  values: T,
): T => {
  const next = { ...values } as T;
  for (const field of nullableTextFields) {
    if (hasOwn(values, field) && typeof next[field] === 'string') {
      (next as Record<string, unknown>)[field] =
        (next[field] as string).trim() || null;
    }
  }
  for (const field of nullableValueFields) {
    if (hasOwn(values, field) && next[field] === undefined) {
      (next as Record<string, unknown>)[field] = null;
    }
  }
  return next;
};

const same = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

/** Edit requests contain only real changes so unchanged, frozen terms cannot block profile edits. */
export const buildFundUpdatePayload = (
  values: FundFormValues,
  initial: FundFormValues,
): Partial<FundFormValues> => {
  const current = normalizeFundFormValues(values);
  const baseline = normalizeFundFormValues(initial);
  return Object.fromEntries(
    Object.entries(current).filter(
      ([key, value]) => !same(value, baseline[key as keyof FundFormValues]),
    ),
  ) as Partial<FundFormValues>;
};
